import { useState, useMemo } from 'react'
import type { JSX } from 'react'

import type { TimeZone } from '@vvo/tzdb'
import { Command } from 'cmdk'

import {
  DropdownMenu,
  GlobeIcon,
  Input,
  SearchIcon,
  toast,
  useLocalStorage,
  Text,
  CheckIcon,
} from '@buffer-mono/popcorn'

import { ALL_CHANNELS_TIMEZONE_STORAGE_KEY } from '~publish/components/AllChannelsTimeZoneSelect'
import { useTimezoneSuggestions } from '~publish/components/TimeZoneSelect/hooks'
import { formatOffset } from '~publish/components/TimeZoneSelect/timezone-formatters'
import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'

import styles from './TimezoneSubMenu.module.css'

function formatTimezoneLabel(timezone: TimeZone): string {
  const city =
    timezone.name.split('/')[1]?.replace(/_/g, ' ') ?? timezone.mainCities[0]
  return city
}

export function TimezoneSubMenu(): JSX.Element {
  const [searchValue, setSearchValue] = useState('')
  const [timezone, setTimezone] = useLocalStorage(
    ALL_CHANNELS_TIMEZONE_STORAGE_KEY,
    CURRENT_TIME_ZONE,
  )

  const [suggestions, allTimezones] = useTimezoneSuggestions(searchValue)

  const filteredTimezones = useMemo(() => {
    if (!searchValue) return allTimezones

    const search = searchValue.toLowerCase()
    return allTimezones.filter(
      (tz) =>
        tz.name.toLowerCase().includes(search) ||
        tz.mainCities.some((city) => city.toLowerCase().includes(search)),
    )
  }, [allTimezones, searchValue])

  const handleSelect = (newTimezone: string): void => {
    if (newTimezone && newTimezone !== timezone) {
      setTimezone(newTimezone)
      toast.success(
        `Time zone updated! Times in this view will display in ${newTimezone.replace(
          /_/g,
          ' ',
        )}`,
      )
    }
  }

  const renderTimezoneItem = (tz: TimeZone): JSX.Element => (
    <Command.Item
      key={tz.name}
      value={`${tz.name} ${tz.mainCities.join(' ')}`}
      onSelect={(): void => handleSelect(tz.name)}
      className={styles.item}
    >
      <span className={styles.checkIcon}>
        {timezone === tz.name && <CheckIcon size="small" />}
      </span>
      <span className={styles.timezoneName}>{formatTimezoneLabel(tz)}</span>
      <Text color="subtle" size="sm">
        ({formatOffset(tz.currentTimeOffsetInMinutes)})
      </Text>
    </Command.Item>
  )

  const currentCity = timezone.split('/')[1]?.replace(/_/g, ' ') ?? timezone

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <GlobeIcon /> {currentCity}
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        <Command loop className={styles.command}>
          <Command.Input asChild>
            <Input
              prefix={<SearchIcon />}
              placeholder="Search timezones..."
              className={styles.searchInput}
              value={searchValue}
              onChange={(e): void => setSearchValue(e.target.value)}
            />
          </Command.Input>
          <Command.List className={styles.list}>
            <Command.Empty className={styles.empty}>
              No timezones found
            </Command.Empty>

            {!searchValue && suggestions.length > 0 && (
              <Command.Group heading="Suggestions" className={styles.group}>
                {suggestions.map(renderTimezoneItem)}
              </Command.Group>
            )}

            <Command.Group
              heading={searchValue ? undefined : 'All timezones'}
              className={styles.group}
            >
              {filteredTimezones.map(renderTimezoneItem)}
            </Command.Group>
          </Command.List>
        </Command>
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}
